import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import * as api from '../lib/api'
import { DATE_FORMAT_LABEL, DATE_TIME_FORMAT_LABEL, formatDate, formatDateTime } from '../lib/dateFormat'
import {
  configuredRatingLabel,
  getConfiguredOverallRatingValue,
  hasObjectiveText as hasConfiguredObjectiveText,
  objectivePriorityTotal,
  useFormStructure,
  usesConfiguredAppraisalLayout
} from './useFormStructure'
import { approverName, personLabel, StatusBadge } from './HRDashboard'
import { Card, Spin, Alert, Typography, Descriptions, Row, Col, Tag, Button, Space, Tabs, Collapse } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import AppraisalPdfButton from './AppraisalPdfButton'
import ReturnHistory from './ReturnHistory'
import BackButton from './BackButton'
import { businessUnitThemeVars, getBusinessUnitTheme } from './businessUnitTheme'
import { getConfiguredRatingDisplay, getConfiguredRatingFullLabel, getConfiguredRatingOptions, getRatingGuideData, getRatingValueDisplay, usesRatingGuideTable } from './ratingScale'
import RatingGuideTable from './RatingGuideTable'
import {
  getPrcSuccessIndicatorValue,
  isPrcAppraisal,
  prcEmployeeInformationFields,
  prcEmployeeInformationTitle,
  prcEvaluationLevel,
  prcObjectiveConfig,
  prcObjectiveScoreLabel
} from './prcFlow'
import PrcRatingGuideDescription from './PrcRatingGuideDescription'
import {
  CultureValuesCheckboxes,
  NextYearObjectivesCard,
  OverallPerformanceRatingCheckboxes,
  checkboxRatingGuideSource,
  isMilAsiaCheckboxFlow
} from './milAsiaCheckboxFlow'
import PerformanceFactorsCard from './PerformanceFactorsCard'
import { competencySuggestedRating, objectiveSuggestedRating, overallAppraisalAverage, overallSuggestedRating, weightedObjectiveAverage } from './objectiveScores'
import { MilwaukeeAsiaEnglishSections, isConfiguredRegionalForm, renderBoldMarkup } from './milwaukeeAsiaEnglish'
import SuggestedRatingBand from './SuggestedRatingBand'
import CalculatedRatingBand from './CalculatedRatingBand'

const { Title, Text, Paragraph } = Typography

const sortObjectivesByPriority = objectives => {
  const rows = [...objectives]
  if (rows.some(row => Number.isNaN(Number(row?.priority || 0)))) return rows
  return rows.sort((left, right) => Number(right?.priority || 0) - Number(left?.priority || 0))
}

const RatingGuideDescription = ({ source }) => {
  const { conversionGuide, descriptions } = getRatingGuideData(source)
  if (usesRatingGuideTable(source)) return <RatingGuideTable source={source} />

  return (
    <div className='tti-rating-guide'>
      <Text strong className='tti-rating-guide-heading'>Performance Rating Conversion</Text>
      <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
        {conversionGuide.map(item => (
          <Col span={6} key={item.code}>
            <Text strong>{item.code}:</Text> {item.rating} ({item.range})
          </Col>
        ))}
      </Row>

      <div style={{ marginTop: 16 }}>
        <Text strong className='tti-rating-guide-heading'>Performance Rating Description</Text>
        <div style={{ marginTop: 8 }}>
          {descriptions.map(item => (
            <div key={item.code} style={{ marginBottom: 8 }}>
              <Text strong>{item.code} ({item.score}) - {item.rating}:</Text> {item.description}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Form structure with categories and questions
const FORM_STRUCTURE = [
  {
    id: 'quality_of_work',
    title: 'Category 1: Quality of Work',
    questions: [
      { id: 'q1', text: 'Pursues quality and achieves excellence in all tasks and goals.' },
      {
        id: 'q2',
        text: 'Takes initiative and produces work consistent with defined organizational standards for the position.'
      },
      { id: 'q3', text: 'Diligently attends to detail; performs tasks with accuracy, completeness and high quality.' },
      { id: 'q4', text: 'Addresses problems and overcomes obstacles successfully to timely completion of tasks.' },
      { id: 'q5', text: 'Demonstrates self-reliance and accomplishes work objectives with minimal supervision.' }
    ]
  },
  {
    id: 'work_attitude',
    title: 'Category 2: Work Attitude',
    questions: [
      { id: 'q1', text: 'Demonstrates the attitude of ownership, and creativity to accomplish tasks.' },
      { id: 'q2', text: 'Takes ownership for the success and quality of work produced.' },
      {
        id: 'q3',
        text: 'Accepts responsibilities for own behaviors, faces up to problems, acknowledges and corrects mistakes.'
      },
      { id: 'q4', text: 'Embraces diverse perspectives to promote or nurture innovation.' },
      { id: 'q5', text: 'Generates new ideas, solutions and creative approaches.' }
    ]
  },
  {
    id: 'knowledge',
    title: 'Category 3: Knowledge',
    questions: [
      {
        id: 'q1',
        text: 'Possesses requisite knowledge and skills for assigned roles and responsibilities. Willing to acquire new skills for self development and advancement.'
      },
      {
        id: 'q2',
        text: 'Demonstrates relevant job knowledge and essential skills in performing duties and functions of the position.'
      },
      { id: 'q3', text: 'Displays a high understanding of all work practices, policies, and procedures.' },
      {
        id: 'q4',
        text: 'Actively participates in appropriate learning and training activities that expand job knowledge and effectiveness.'
      },
      { id: 'q5', text: 'Shares information and expertise to assist others to accomplish work assignments.' }
    ]
  },
  {
    id: 'communication',
    title: 'Category 4: Communication',
    questions: [
      { id: 'q1', text: 'Listens to others and communicates with clarity and effectiveness.' },
      {
        id: 'q2',
        text: 'Provides regular, consistent and meaningful information and ideas with others to make sure all appropriate individuals are informed.'
      },
      { id: 'q3', text: 'Communicates in a clear and concise manner using appropriate language, tone, and structure.' },
      {
        id: 'q4',
        text: 'Listens carefully to others, opens to other points of view, asks questions for clarification and ensures what others have said is understood.'
      },
      { id: 'q5', text: 'Gives and receives constructive feedback to foster effective working relationships.' }
    ]
  },
  {
    id: 'planning_organizing',
    title: 'Category 5: Planning & Organizing',
    questions: [
      { id: 'q1', text: 'Identifies and priorities tasks, formulates and executes plans to achieve objectives.' },
      { id: 'q2', text: 'Manages multiple duties and tasks effectively and efficiently.' },
      {
        id: 'q3',
        text: 'Identifies and organizes the varied resources needed to achieve goals and prioritizes key action steps.'
      },
      {
        id: 'q4',
        text: 'Prepares clear plans with strategic focus, specific milestones and deadlines prior to initiative project work.'
      },
      { id: 'q5', text: 'Develops contingency plans for potential problems or drastic changes.' }
    ]
  },
  {
    id: 'interaction_with_others',
    title: 'Category 6: Interaction with Others',
    questions: [
      { id: 'q1', text: 'Positively interacts with a diverse workforce.' },
      {
        id: 'q2',
        text: 'Works with team members and others in ways that strengthen optimal team interaction and results.'
      },
      { id: 'q3', text: 'Encourages and supports colleagues in expressing ideas and sharing information.' },
      {
        id: 'q4',
        text: 'Cultivates positive relationships among team members by actively contributing to team processes and deliverables, such as team meetings and assignments.'
      },
      { id: 'q5', text: 'Demonstrates understanding, courtesy, empathy and genuine concern in all interactions.' }
    ]
  }
]

const getRatingCategory = (value) => {
  const rating = parseFloat(value)
  if (!rating) return null
  if (rating <= 1.5) return { code: 'NI', rating: 'Needs Improvement' }
  if (rating <= 1.75) return { code: 'ME-', rating: 'Meets Expectations-' }
  if (rating <= 2.25) return { code: 'ME', rating: 'Meets Expectations' }
  if (rating <= 2.5) return { code: 'ME+', rating: 'Meets Expectations+' }
  if (rating <= 2.75) return { code: 'EE-', rating: 'Exceeds Expectations-' }
  if (rating <= 3.25) return { code: 'EE', rating: 'Exceeds Expectations' }
  if (rating <= 3.5) return { code: 'EE+', rating: 'Exceeds Expectations+' }
  return { code: 'E', rating: 'Excels' }
}

const getRatingCategoryDisplay = (value) => {
  const category = getRatingCategory(value)
  return category ? `${category.code} - ${category.rating}` : null
}

const hasObjectiveText = objective => Boolean((objective?.objective || '').trim())
const hasCommentText = comment => Boolean(String(comment || '').trim())
const wasReleasedByHR = comment => ['Released by local HR as the ', 'Released by HR:', 'Auto-approved by HR:'].some(
  prefix => comment?.trim().startsWith(prefix)
)

export default function AppraisalDetail({ appraisal: providedAppraisal = null, children = null, translationReview = false, approvalHistoryMinLevel = 0, approvalHistoryMaxLevel = 5, showReturnContext = false, returnResponse = null, activeFunctionalReviewId = null, onFunctionalReviewTabChange = null }) {
  const { id } = useParams()
  const [appraisal, setAppraisal] = useState(providedAppraisal)
  const [loading, setLoading] = useState(!providedAppraisal)
  const firstApproverName = approverName(appraisal, 1)
  const secondApproverName = approverName(appraisal, 2)
  const thirdApproverName = approverName(appraisal, 3)
  const fourthApproverName = approverName(appraisal, 4)
  const fifthApproverName = approverName(appraisal, 5)
  const hrReleaseComment = `Released by local HR as the ${appraisal?.session_display_title || 'performance appraisal'} cycle is now closed`

  const renderApprovalHistory = () => {
    const entries = [
      {
        level: 0,
        role: 'Employee',
        name: personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template) || 'Employee',
        action: 'Submitted',
        timestamp: appraisal.employee_submitted_at,
        comments: appraisal.employee_overall_comment
      },
      {
        level: 1,
        role: 'Approver 1',
        name: firstApproverName || 'Approver 1',
        action: 'Submitted review',
        timestamp: appraisal.manager_submitted_at,
        comments: appraisal.manager_overall_comment,
        releasedByHR: wasReleasedByHR(appraisal.manager_overall_comment)
      },
      {
        level: 2,
        role: 'Approver 2',
        name: secondApproverName || 'Approver 2',
        action: 'Signed off',
        timestamp: appraisal.head_signed_off_at,
        comments: appraisal.head_comments,
        releasedByHR: wasReleasedByHR(appraisal.head_comments)
      },
      {
        level: 3,
        role: 'Approver 3',
        name: thirdApproverName || 'Approver 3',
        action: 'Signed off',
        timestamp: appraisal.bu_head_signed_off_at,
        comments: appraisal.bu_head_comments,
        releasedByHR: wasReleasedByHR(appraisal.bu_head_comments)
      },
      {
        level: 4,
        role: 'Approver 4',
        name: fourthApproverName || 'Approver 4',
        action: 'Signed off',
        timestamp: appraisal.approver_4_signed_off_at,
        comments: appraisal.approver_4_comments,
        releasedByHR: wasReleasedByHR(appraisal.approver_4_comments)
      },
      {
        level: 5,
        role: 'Approver 5',
        name: fifthApproverName || 'Approver 5',
        action: 'Signed off',
        timestamp: appraisal.approver_5_signed_off_at,
        comments: appraisal.approver_5_comments,
        releasedByHR: wasReleasedByHR(appraisal.approver_5_comments)
      }
    ].filter(entry => translationReview || (entry.level >= approvalHistoryMinLevel && entry.level <= approvalHistoryMaxLevel && (
      entry.role === 'Employee' || entry.role === 'Approver 1' ||
      (entry.role === 'Approver 2' && appraisal.approver_2) ||
      (entry.role === 'Approver 3' && appraisal.approver_3) ||
      (entry.role === 'Approver 4' && appraisal.approver_4) ||
      (entry.role === 'Approver 5' && appraisal.approver_5)
    )))

    return (
      <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Reviewed / Approvals</Text>} style={{ marginBottom: 24 }}>

        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <Descriptions column={1} size='small'>
            <Descriptions.Item label='Appraisal Status'><StatusBadge status={appraisal.status} label={appraisal.status_label} /></Descriptions.Item>
            <Descriptions.Item label='Overall Performance Rating'>
              {configuredRatingLabel(
                getConfiguredOverallRatingValue(formData, formConfig) ||
                formData.manager_overall_performance_rating || formData.overall_manager_rating,
                formConfig
              )}
            </Descriptions.Item>
          </Descriptions>
          {entries.map(entry => (
            <Card key={entry.role} size='small' type='inner' title={entry.role}>
              <Descriptions column={1} size='small'>
                <Descriptions.Item label={entry.role === 'Employee' ? 'Submitted By' : 'Approver'}>{entry.name}</Descriptions.Item>
                <Descriptions.Item label='Status'>{entry.timestamp && !entry.releasedByHR ? entry.action : 'Pending'}</Descriptions.Item>
                <Descriptions.Item label='Date'>{entry.timestamp && !entry.releasedByHR ? formatDateTime(entry.timestamp) : 'Not recorded'}</Descriptions.Item>
                {(entry.releasedByHR || hasCommentText(entry.comments)) && (
                  <Descriptions.Item label='Comments'>{entry.releasedByHR ? hrReleaseComment : entry.comments}</Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          ))}
        </Space>
      </Card>
    )
  }

  useEffect(() => {
    if (providedAppraisal) {
      setAppraisal(providedAppraisal)
      setLoading(false)
      return
    }
    setLoading(true)
    api.getAppraisal(id, { history_scope: 'hr' })
      .then(setAppraisal)
      .finally(() => setLoading(false))
  }, [id, providedAppraisal])
  const {
    formStructure,
    formConfig,
    loading: structureLoading,
    error: structureError
  } = useFormStructure(
    appraisal?.session_template || appraisal?.session_business_unit,
    FORM_STRUCTURE,
    { translationReview }
  )

  if (loading || structureLoading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
  if (!appraisal) return <Alert type="error" message="Appraisal not found" />
  if (structureError) {
    return <Alert type="error" showIcon message="Unable to load the appraisal template" description="Refresh the page or contact HR if the problem continues." />
  }

  const formData = appraisal.form_data || {}
  const objectivesConfig = formConfig?.objectives || {}
  const objectivePriorityOptions = Array.isArray(objectivesConfig.priority_options) ? objectivesConfig.priority_options : []
  const categoryAverageLabel = formConfig?.competencies?.category_average_label || 'Calculated Rating'
  const usesObjectivePriorityOptions = objectivePriorityOptions.length > 0
  const objectivePriorityDisplay = priority => {
    if (priority === null || priority === undefined || priority === '') return '—'
    if (!usesObjectivePriorityOptions) return `${priority}%`
    return objectivePriorityOptions.find(option => `${option.value}` === `${priority}`)?.label || priority
  }
  const objectives = sortObjectivesByPriority(
    (formData.annual_objectives || []).filter(objective => (
      hasObjectiveText(objective) ||
      (
        objectivesConfig.allow_partial_average &&
        (
          (objective?.self_score !== null && objective?.self_score !== undefined && objective?.self_score !== '') ||
          (objective?.manager_score !== null && objective?.manager_score !== undefined && objective?.manager_score !== '')
        )
      )
    ))
  )
  const showObjectiveAverageScore = objectivesConfig.show_average_score !== false
  const showCategoryAverageAsRating = formConfig?.competencies?.category_average_display === 'rating_name'
  const competencySummaryTitle = formConfig?.competencies?.summary_title || 'Competency Average Score'
  const competencySummarySubtitle = formConfig?.competencies?.summary_subtitle
  const singleCategoryComment = appraisal?.session_template === 'vietnam_milwaukee' && formConfig?.competencies?.single_category_comment === true
  const categoryCommentQuestionKey = formConfig?.competencies?.category_comment_question_key || 'q1'
  const categoryCommentLabel = formConfig?.competencies?.category_comment_label || 'Comment'
  const overallPerformanceTitle = formConfig?.overall_performance?.summary_title || 'Overall Average Score'
  const competencyManualRating = formConfig?.competencies?.manual_rating || {}
  const overallManualRating = formConfig?.overall_performance?.manual_rating || {}
  const employeeCompetencyRatingField = competencyManualRating.employee_field || 'employee_competency_overall_rating'
  const managerCompetencyRatingField = competencyManualRating.manager_field || 'manager_competency_overall_rating'
  const employeeOverallRatingField = overallManualRating.employee_field || 'employee_overall_performance_rating'
  const managerOverallRatingField = overallManualRating.manager_field || 'manager_overall_performance_rating'
  const showCompetencySummarySubtext = formConfig?.competencies?.show_summary_subtext !== false
  const showOverallSummarySubtext = formConfig?.overall_performance?.show_summary_subtext !== false
  const manualRatingDisplay = field => getConfiguredRatingFullLabel(
    getConfiguredRatingOptions(formConfig).find(option => option.value === Number(formData[field]))
  ) || '--'
  const objectiveSelfAvg = weightedObjectiveAverage(
    objectives,
    'self_score',
    objectivesConfig.average_mode,
    objectivesConfig.allow_partial_average
  )
  const objectiveManagerAvg = weightedObjectiveAverage(
    objectives,
    'manager_score',
    objectivesConfig.average_mode,
    objectivesConfig.allow_partial_average
  )
  const objectiveSummaryConfig = objectivesConfig.summary_rating || {}
  const objectiveSummaryEnabled = Boolean(objectiveSummaryConfig.enabled)
  const employeeObjectiveRatingField = objectiveSummaryConfig.employee_field || 'employee_objective_overall_rating'
  const managerObjectiveRatingField = objectiveSummaryConfig.manager_field || 'manager_objective_overall_rating'
  const employeeObjectiveSuggestedRating = objectiveSuggestedRating(
    objectives,
    'self_score',
    objectiveSelfAvg,
    objectivesConfig
  )
  const managerObjectiveSuggestedRating = objectiveSuggestedRating(
    objectives,
    'manager_score',
    objectiveManagerAvg,
    objectivesConfig
  )
  const totalPriority = objectives.reduce((sum, objective) => sum + (parseFloat(objective.priority) || 0), 0)
  const hasValidPriorityTotal = Math.abs(totalPriority - 100) < 0.01
  const getFieldValue = (categoryId, questionId, field) => {
    return formData[categoryId]?.[questionId]?.[field] || (field.includes('rating') ? null : '')
  }

  const calculateCategoryAverage = (categoryId, ratingField) => {
    const category = formStructure.find(c => c.id === categoryId)
    if (!category) return null

    const ratings = category.questions.map(q => getFieldValue(categoryId, q.id, ratingField))
    if (ratings.length === 0 || ratings.some(rating => rating === null || rating === '' || rating === undefined)) return null

    const sum = ratings.reduce((acc, rating) => acc + parseFloat(rating), 0)
    return (sum / ratings.length).toFixed(2)
  }

  const calculateOverallCategoryAverage = (ratingField) => {
    const categoryAverages = formStructure.map(category => calculateCategoryAverage(category.id, ratingField))
    if (categoryAverages.length === 0 || categoryAverages.some(average => average === null)) return null
    return (categoryAverages.reduce((sum, average) => sum + parseFloat(average), 0) / categoryAverages.length).toFixed(2)
  }

  const categoryRatings = (categoryId, ratingField) => {
    const category = formStructure.find(item => item.id === categoryId)
    return category?.questions.map(question => getFieldValue(categoryId, question.id, ratingField)) || []
  }

  const allCompetencyRatings = ratingField => (
    formStructure.flatMap(category => categoryRatings(category.id, ratingField))
  )

  const calculateCategorySuggestedRating = (categoryId, ratingField) => competencySuggestedRating(
    categoryRatings(categoryId, ratingField),
    calculateCategoryAverage(categoryId, ratingField),
    formConfig
  )

  const overallEmployeeCategoryAvg = calculateOverallCategoryAverage('employee_rating')
  const overallManagerCategoryAvg = calculateOverallCategoryAverage('manager_rating')
  const overallEmployeeAverage = overallAppraisalAverage(objectiveSelfAvg, overallEmployeeCategoryAvg)
  const overallManagerAverage = overallAppraisalAverage(objectiveManagerAvg, overallManagerCategoryAvg)
  const employeeCompetencySuggestedRating = competencySuggestedRating(
    allCompetencyRatings('employee_rating'),
    overallEmployeeCategoryAvg,
    formConfig
  )
  const managerCompetencySuggestedRating = competencySuggestedRating(
    allCompetencyRatings('manager_rating'),
    overallManagerCategoryAvg,
    formConfig
  )
  const employeeOverallSuggestedRating = overallSuggestedRating(
    employeeObjectiveSuggestedRating,
    employeeCompetencySuggestedRating,
    overallEmployeeAverage,
    formConfig
  )
  const managerOverallSuggestedRating = overallSuggestedRating(
    managerObjectiveSuggestedRating,
    managerCompetencySuggestedRating,
    overallManagerAverage,
    formConfig
  )
  const theme = getBusinessUnitTheme(appraisal)
  const signoffBanner = (!children && appraisal.pending_approver_action) ? (
    <Alert
      type='info'
      showIcon
      style={{ marginBottom: 16 }}
      message='This appraisal is awaiting your sign-off.'
      action={(
        <Link to={`/appraisals/${id}/${appraisal.pending_approver_action}`}>
          <Button type='primary'>Provide feedback &amp; sign off</Button>
        </Link>
      )}
    />
  ) : null
  const returnContext = (
    <>
      {appraisal.return_comments && (
        <Alert
          type='warning'
          showIcon
          message={`Returned for changes by ${appraisal.returned_by_name || appraisal.returned_by_email || 'a reviewer'}`}
          description={appraisal.return_comments}
          style={{ marginBottom: 24 }}
        />
      )}
      <ReturnHistory appraisal={appraisal} />
    </>
  )
  const isConfiguredLayout = usesConfiguredAppraisalLayout(formConfig)
  const isPrc = isPrcAppraisal(appraisal)
  const usesConfiguredRatings = Array.isArray(formConfig.rating_options) && formConfig.rating_options.length > 0
  const isCheckboxFlow = isMilAsiaCheckboxFlow(appraisal, formConfig)
  const ratingGuideSource = checkboxRatingGuideSource(appraisal, formConfig)
  const objectiveScoreLabel = prcObjectiveScoreLabel(appraisal, formConfig)
  const prcObjectives = prcObjectiveConfig(formConfig)
  const prcSuccessIndicatorLevels = prcObjectives.success_indicator_levels || []
  const prcEmployeeInfoFields = prcEmployeeInformationFields(appraisal, formConfig)
  const renderEmployeeInformation = children => (
    <Collapse
      className='tti-collapsible-form-card'
      style={{ marginBottom: 24 }}
      expandIconPlacement='end'
      items={[{
        key: 'employee-information',
        label: (
          <Space wrap>
            <Text strong>{isPrc ? prcEmployeeInformationTitle(formConfig) : 'Employee Information'}</Text>
            <Text type='secondary'>
              {[personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template), appraisal.employee_position, appraisal.department_name].filter(Boolean).join(' · ')}
            </Text>
          </Space>
        ),
        children
      }]}
    />
  )
  const getAppraisalRatingDisplay = value => {
    if (!value) return null
    if (isPrc) return `${value} - ${prcEvaluationLevel(value)}`
    return usesConfiguredRatings ? getConfiguredRatingDisplay(value, formConfig) : getRatingValueDisplay(value, ratingGuideSource)
  }
  const getAppraisalRatingCategoryDisplay = value => {
    if (!value) return null
    return isPrc ? prcEvaluationLevel(value) : getRatingCategoryDisplay(value)
  }
  const functionalReviews = appraisal.functional_reviews || []
  const renderFunctionalReviewFeedback = reviews => {
    if (!reviews.length) return null

    return (
      <Card
        className='tti-appraisal-section-card'
        title={<Text style={{ color: '#FFF' }}>Indirect Manager Feedback</Text>}
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {reviews.map(review => {
            const reviewData = review.form_data || {}
            const reviewObjectives = sortObjectivesByPriority(
              (isConfiguredLayout ? (reviewData.objectives || []) : (reviewData.annual_objectives || [])).filter(hasObjectiveText)
            )
            const reviewNextYearObjectives = sortObjectivesByPriority(
              (reviewData.next_year_objectives || []).filter(hasConfiguredObjectiveText)
            )
            const getReviewFieldValue = (categoryId, questionId, field) => (
              reviewData[categoryId]?.[questionId]?.[field] || (field.includes('rating') ? null : '')
            )

            return (
              <Card
                key={review.id}
                size="small"
                type="inner"
                title={review.reviewer_name || review.reviewer_email || 'Indirect reviewer'}
              >
                <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label="Status">{review.status}</Descriptions.Item>
                  <Descriptions.Item
                    label="Submitted">{review.submitted_at ? formatDateTime(review.submitted_at) : 'Pending'}</Descriptions.Item>
                  <Descriptions.Item label="Requested by">{review.requested_by_email || '—'}</Descriptions.Item>
                  <Descriptions.Item
                    label="Last updated">{review.updated_at ? formatDateTime(review.updated_at) : '—'}</Descriptions.Item>
                </Descriptions>

                {hasCommentText(review.manager_overall_comment) && (
                  <Card size="small" style={{ marginBottom: 12 }} title="Overall Comment">
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{review.manager_overall_comment}</Text>
                  </Card>
                )}

                {isConfiguredLayout && (
                  <>
                    <Card size="small" style={{ marginBottom: 12 }} title={<Text style={{ color: '#FFF' }}>Appraisal Process Feedback</Text>}>
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Overall Performance Rating">
                          {configuredRatingLabel(getConfiguredOverallRatingValue(reviewData, formConfig), formConfig)}
                        </Descriptions.Item>
                      </Descriptions>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {formStructure.map(category => {
                          const rating = getReviewFieldValue(category.id, 'q1', 'manager_rating')
                          const comments = getReviewFieldValue(category.id, 'q1', 'manager_comments')
                          return (
                            <Card key={category.id} size="small" type="inner" title={category.title}>
                              <Descriptions column={1} size="small">
                                <Descriptions.Item
                                  label="Rating">{configuredRatingLabel(rating, formConfig)}</Descriptions.Item>
                                <Descriptions.Item
                                  label="Examples">{comments || 'No examples provided'}</Descriptions.Item>
                              </Descriptions>
                            </Card>
                          )
                        })}
                      </Space>
                    </Card>

                    <Card size="small" title={<Text style={{ color: '#FFF' }}>Next Year's Objectives</Text>}>
                      {reviewNextYearObjectives.length
                        ? reviewNextYearObjectives.map((objective, index) => (
                          <Row key={index} gutter={12} style={{ marginBottom: 8 }}>
                            <Col span={4}><Tag color="green">{objective.priority || 0}%</Tag></Col>
                            <Col span={20}><Text style={{ whiteSpace: 'pre-wrap' }}>{objective.objective}</Text></Col>
                          </Row>
                        ))
                        : <Text type="secondary">No next-year objectives provided.</Text>}
                    </Card>
                  </>
                )}

                {!isConfiguredLayout && (
                  <>
                    <Card size="small" style={{ marginBottom: 12 }} title={<Text style={{ color: '#FFF' }}>Annual Objectives Feedback</Text>}>
                      {reviewObjectives.length
                        ? reviewObjectives.map((objective, index) => (
                          <div key={index} style={{
                            marginBottom: 12,
                            paddingBottom: 12,
                            borderBottom: index < reviewObjectives.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}>
                            <Row gutter={8} align="top">
                              <Col span={2}><Tag color="blue">{objectivePriorityDisplay(objective.priority)}</Tag></Col>
                              <Col span={8}><Text style={{ whiteSpace: 'pre-wrap' }}>{objective.objective || '—'}</Text></Col>
                              <Col span={5}>
                                <Text strong style={{ display: 'block', fontSize: 12 }}>Indirect Manager Rating</Text>
                                <Text>{getAppraisalRatingDisplay(objective.manager_score) || 'Not rated'}</Text>
                              </Col>
                              <Col span={9}>
                                <Text strong style={{ display: 'block', fontSize: 12 }}>Indirect Manager Comment</Text>
                                <Text type="secondary"
                                  style={{ whiteSpace: 'pre-wrap' }}>{objective.manager_comment || 'No comment'}</Text>
                              </Col>
                            </Row>
                            {objectiveSummaryEnabled && (
                              <>
                                <SuggestedRatingBand items={[
                                  { label: 'Employee Calculated Suggested Rating', value: employeeObjectiveSuggestedRating },
                                  { label: 'Manager Calculated Suggested Rating', value: managerObjectiveSuggestedRating }
                                ]} />
                                <Row gutter={8} align="middle" style={{ marginTop: 12 }}>
                                  <Col span={8}><Text strong>Overall Rating of Objective Appraisal (manual)</Text></Col>
                                  <Col span={8}>
                                    <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                                      <Text strong>
                                        {getConfiguredRatingFullLabel(
                                          getConfiguredRatingOptions(formConfig).find(
                                            option => option.value === Number(formData[employeeObjectiveRatingField])
                                          )
                                        ) || '--'}
                                      </Text>
                                    </div>
                                  </Col>
                                  <Col span={8}>
                                    <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-secondary-bg)', borderRadius: 4 }}>
                                      <Text strong>
                                        {getConfiguredRatingFullLabel(
                                          getConfiguredRatingOptions(formConfig).find(
                                            option => option.value === Number(formData[managerObjectiveRatingField])
                                          )
                                        ) || '--'}
                                      </Text>
                                    </div>
                                  </Col>
                                </Row>
                              </>
                            )}
                          </div>
                        ))
                        : <Text type="secondary">No annual objective feedback provided.</Text>}
                    </Card>

                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {formStructure.map(category => (
                        <Card key={category.id} size="small" type="inner" title={category.title}>
                          {category.questions.map((question, index) => {
                            const rating = getReviewFieldValue(category.id, question.id, 'manager_rating')
                            const comments = getReviewFieldValue(category.id, question.id, 'manager_comments')
                            return (
                              <div
                                key={question.id}
                                style={{
                                  marginBottom: 10,
                                  paddingBottom: 10,
                                  borderBottom: index < category.questions.length - 1 ? '1px solid #f0f0f0' : 'none'
                                }}
                              >
                                <Paragraph strong style={{
                                  marginBottom: 6,
                                  fontSize: 15,
                                  lineHeight: 1.5
                                }}>{index + 1}. {question.text}</Paragraph>
                                <Row gutter={12}>
                                  <Col span={singleCategoryComment ? 24 : 8}>
                                    <Text strong style={{ fontSize: 12 }}>Rating</Text>
                                    <div>{getAppraisalRatingDisplay(rating) || 'Not rated'}</div>
                                  </Col>
                                  {!singleCategoryComment && (
                                    <Col span={16}>
                                      <Text strong style={{ fontSize: 12 }}>Comment</Text>
                                      <div><Text type="secondary"
                                        style={{ whiteSpace: 'pre-wrap' }}>{comments || 'No comment'}</Text>
                                      </div>
                                    </Col>
                                  )}
                                </Row>
                              </div>
                            )
                          })}
                          {singleCategoryComment && category.questions.some(question => question.id === categoryCommentQuestionKey) && (
                            <div style={{ marginTop: 12 }}>
                              <Text strong style={{ fontSize: 12 }}>{categoryCommentLabel}</Text>
                              <div>
                                <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                                  {getReviewFieldValue(category.id, categoryCommentQuestionKey, 'manager_comments') || 'No comment'}
                                </Text>
                              </div>
                            </div>
                          )}
                        </Card>
                      ))}
                    </Space>
                  </>
                )}
              </Card>
            )
          })}
        </Space>
      </Card>
    )
  }
  const renderAppraisalTabs = appraisalContent => {
    if (!functionalReviews.length) return appraisalContent
    const activeTabKey = activeFunctionalReviewId
      ? `functional-manager-feedback-${activeFunctionalReviewId}`
      : 'appraisal-form'
    return (
      <Tabs
        activeKey={onFunctionalReviewTabChange ? activeTabKey : undefined}
        onChange={onFunctionalReviewTabChange
          ? key => onFunctionalReviewTabChange(
              key === 'appraisal-form' ? null : key.replace('functional-manager-feedback-', '')
            )
          : undefined}
        items={[
          { key: 'appraisal-form', label: 'Appraisal Form', children: appraisalContent },
          ...functionalReviews.map((review, index) => ({
            key: `functional-manager-feedback-${review.id}`,
            label: review.reviewer_name || review.reviewer_email || `Reviewer ${index + 1}`,
            children: renderFunctionalReviewFeedback([review])
          }))
        ]}
      />
    )
  }

  if (isConfiguredLayout) {
    const configuredObjectives = sortObjectivesByPriority(
      (formData.objectives || []).filter(hasConfiguredObjectiveText)
    )
    const configuredTotalPriority = objectivePriorityTotal(configuredObjectives)
    const hasValidConfiguredPriorityTotal = Math.abs(configuredTotalPriority - 100) < 0.01
    const nextYearObjectives = sortObjectivesByPriority(
      (formData.next_year_objectives || []).filter(hasConfiguredObjectiveText)
    )
    const nextYearTotal = objectivePriorityTotal(nextYearObjectives)
    const configuredForm = formConfig.appraisal_form || {}
    const showEnglishSections = isConfiguredRegionalForm(formConfig)
    const employeeRatingField = configuredForm.employee_overall_rating_field || 'employee_overall_rating'
    const hasAchievementExamples = Object.prototype.hasOwnProperty.call(
      configuredForm.objective_row || {},
      'achievement_examples'
    ) || /(^|_)tti$/.test(appraisal.session_template || '') ||
      configuredObjectives.some(row => Object.prototype.hasOwnProperty.call(row, 'achievement_examples'))
    return (
      <div className='tti-appraisal-form' style={{ maxWidth: 1600, margin: '0 auto', padding: '20px', ...businessUnitThemeVars(theme) }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton type='text' fallback='/dashboard' className='tti-appraisal-back-button' aria-label='Back' />
        </div>
        {signoffBanner}

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 24
        }}>
          <div>
            {theme.logoUrl && <img src={theme.logoUrl} alt={appraisal.session_template_name || 'Template logo'} style={{
              display: 'block',
              maxHeight: 56,
              maxWidth: 160,
              objectFit: 'contain',
              marginBottom: 12
            }} />}
            <div>
              <Title level={2} className='tti-page-title' style={{ margin: 0 }}>{appraisal.session_display_title || '2026 Performance Appraisal'}</Title>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' }}>
            <AppraisalPdfButton appraisalId={appraisal.id} status={appraisal.status} canExport={appraisal.can_export_pdf} type="primary">Export PDF</AppraisalPdfButton>
            <StatusBadge status={appraisal.status} label={appraisal.status_label} />
          </div>
        </div>

        {showReturnContext && returnContext}
        {showReturnContext && returnResponse}

        {renderAppraisalTabs(
          <>
            {renderEmployeeInformation(
              <Descriptions column={2} size="small">
                <Descriptions.Item
                  label="Employee's Name">{personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}</Descriptions.Item>
                <Descriptions.Item
                  label="Employee's Staff Number">{appraisal.employee_company_id || '--'}</Descriptions.Item>
                <Descriptions.Item label="Position">{appraisal.employee_position || '—'}</Descriptions.Item>
                <Descriptions.Item
                  label="Performance Period">{formatDate(appraisal.effective_review_period_start_date)} - {formatDate(appraisal.effective_review_period_end_date)}</Descriptions.Item>
                <Descriptions.Item
                  label='First approver'>{firstApproverName}</Descriptions.Item>
                <Descriptions.Item label="Department Name">{appraisal.department_name || '—'}</Descriptions.Item>
                {appraisal.session_organization_structure !== 'department_only' && (
                  <Descriptions.Item label="Business Unit">{appraisal.business_unit_name || '—'}</Descriptions.Item>
                )}
              </Descriptions>
            )}

            <Card className='tti-appraisal-section-card' title={<Text style={{ whiteSpace: 'pre-line', color: '#FFF' }}>{formConfig.objectives?.title || 'I. Appraisal Year Objectives'}</Text>} style={{ marginBottom: 24 }}>
              <Row gutter={12}><Col span={4}><Text strong>Priority</Text></Col><Col span={hasAchievementExamples ? 10 : 20}><Text strong>Objective</Text></Col>{hasAchievementExamples && <Col span={10}><Text strong>Examples of Achievement</Text></Col>}</Row>
              {configuredObjectives.length
                ? configuredObjectives.map((objective, index) => (
                  <Row key={index} gutter={12} style={{ marginBottom: 12 }}>
                    <Col span={4}><Tag color="blue">{objective.priority || 0}%</Tag></Col>
                    <Col span={hasAchievementExamples ? 10 : 20}><Text style={{ whiteSpace: 'pre-wrap' }}>{objective.objective}</Text></Col>
                    {hasAchievementExamples && (
                      <Col span={10}>
                        <Text type='secondary' style={{ whiteSpace: 'pre-wrap' }}>
                          {objective.achievement_examples || 'No achievement examples provided.'}
                        </Text>
                      </Col>
                    )}
                  </Row>
                ))
                : <Text type="secondary">No objectives submitted.</Text>}
              {configuredObjectives.length > 0 && (
                <Text type={hasValidConfiguredPriorityTotal ? 'success' : 'danger'} strong>
                  Total Priority: {configuredTotalPriority}%
                </Text>
              )}
            </Card>

            {showEnglishSections
              ? (
                <>
                  <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>{formConfig.employee_comments?.title || 'Employee Comments'}</Text>} style={{ marginBottom: 24 }}>
                    {appraisal.employee_overall_comment
                      ? <Text style={{ whiteSpace: 'pre-wrap' }}>{appraisal.employee_overall_comment}</Text>
                      : <Text type="secondary">No employee comments provided.</Text>}
                  </Card>

                  <MilwaukeeAsiaEnglishSections
                    formData={formData}
                    formConfig={formConfig}
                    mode='read'
                    showDevelopmentPlan={false}
                    showManagerFields={false}
                    cultureSide='employee'
                  />

                  <MilwaukeeAsiaEnglishSections
                    formData={formData}
                    formConfig={formConfig}
                    mode='read'
                    showDevelopmentPlan={false}
                    cultureSide='manager'
                  />

                  <Card className='tti-appraisal-section-card' title={<Text style={{ whiteSpace: 'pre-line', color: '#FFF' }}>{formConfig.next_year_objectives?.title || "Setting Next Year's Objectives"}</Text>} style={{ marginBottom: 24 }}>
                    {formConfig.next_year_objectives?.description && (
                      <Text type='secondary' style={{ display: 'block', marginBottom: 16, whiteSpace: 'pre-line' }}>
                        {formConfig.next_year_objectives.description}
                      </Text>
                    )}
                    {formConfig.next_year_objectives?.objectives_note && (
                      <Text type='secondary' style={{ display: 'block', marginBottom: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0', whiteSpace: 'pre-line' }}>
                        {renderBoldMarkup(formConfig.next_year_objectives.objectives_note)}
                      </Text>
                    )}
                    {nextYearObjectives.length
                      ? nextYearObjectives.map((objective, index) => (
                        <Row key={index} gutter={12} style={{ marginBottom: 12 }}>
                          <Col span={4}><Tag color="blue">{objective.priority || 0}%</Tag></Col>
                          <Col span={20}><Text style={{ whiteSpace: 'pre-wrap' }}>{objective.objective}</Text></Col>
                        </Row>
                      ))
                      : <Text type="secondary">No next-year objectives set yet.</Text>}
                    {nextYearObjectives.length > 0 && (
                      <Text type={Math.abs(nextYearTotal - 100) < 0.01 ? 'success' : 'danger'} strong>
                        Total Priority: {nextYearTotal}%
                      </Text>
                    )}
                  </Card>

                  <MilwaukeeAsiaEnglishSections
                    formData={formData}
                    formConfig={formConfig}
                    mode='read'
                    showCulture={false}
                  />

                  <Card
                    className='tti-appraisal-section-card'
                    title={<Text strong style={{ whiteSpace: 'pre-line', color: '#FFF' }}>{formConfig.appraisal_process?.manager_comments_label || 'Manager Overall Comments'}</Text>}
                    style={{ marginBottom: 24 }}
                  >
                    <Text style={{ whiteSpace: 'pre-wrap' }}>
                      {appraisal.manager_overall_comment || `Pending comments from ${firstApproverName}`}
                    </Text>
                  </Card>

                  <PerformanceFactorsCard
                    formStructure={formStructure}
                    formConfig={formConfig}
                    getFieldValue={getFieldValue}
                    updateField={() => { }}
                    getQuestionRatingOptions={() => []}
                    getRatingOptionLabel={option => option.label}
                    getRatingValueDisplay={value => configuredRatingLabel(value, formConfig)}
                    visibleSide='manager'
                    managerSubmitted={Boolean(appraisal.manager_submitted_at)}
                  />

                  <Card
                    className='tti-appraisal-section-card'
                    title={(
                      <Text strong style={{ whiteSpace: 'pre-line', color: '#FFF' }}>
                        {formConfig.appraisal_process?.employee_overall_rating_title ||
                          `${formConfig.appraisal_process?.overall_rating_label || 'Overall Performance Rating'} - ${formConfig.appraisal_process?.employee_evaluation_label || 'Employee Self Rating'}`}
                      </Text>
                    )}
                    style={{ marginBottom: 24 }}
                  >
                    <Text>{configuredRatingLabel(formData[employeeRatingField], formConfig)}</Text>
                  </Card>

                  <Card
                    className='tti-appraisal-section-card'
                    title={(
                      <Text strong style={{ whiteSpace: 'pre-line', color: '#FFF' }}>
                        {formConfig.appraisal_process?.manager_overall_rating_title ||
                          `${formConfig.appraisal_process?.overall_rating_label || 'Overall Performance Rating'} - ${formConfig.appraisal_process?.manager_evaluation_label || 'Manager Rating'}`}
                      </Text>
                    )}
                    style={{ marginBottom: 24 }}
                  >
                    <Text>{configuredRatingLabel(getConfiguredOverallRatingValue(formData, formConfig), formConfig)}</Text>
                  </Card>

                </>
              )
              : (
                <>
                  <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>II. Appraisal Process</Text>} style={{ marginBottom: 24 }}>
                    <div style={{ marginBottom: 18 }}>
                      <Text strong>Overall Comment by {firstApproverName}</Text>
                      <div style={{
                        marginTop: 6,
                        padding: 12,
                        background: 'var(--neutral-grey0)',
                        borderRadius: 4,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {appraisal.manager_overall_comment || `Pending comments from ${firstApproverName}`}
                      </div>
                    </div>
                    <div style={{ marginBottom: 18 }}>
                      <Text strong>Overall Performance Rating</Text>
                      <div style={{ marginTop: 6 }}>{configuredRatingLabel(getConfiguredOverallRatingValue(formData, formConfig), formConfig)}</div>
                    </div>
                  </Card>

                  <PerformanceFactorsCard
                    formStructure={formStructure}
                    formConfig={formConfig}
                    getFieldValue={getFieldValue}
                    updateField={() => { }}
                    getQuestionRatingOptions={() => []}
                    getRatingOptionLabel={option => option.label}
                    getRatingValueDisplay={value => configuredRatingLabel(value, formConfig)}
                    visibleSide='manager'
                    managerSubmitted={Boolean(appraisal.manager_submitted_at)}
                  />

                  <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>III. Employee's Comments</Text>} style={{ marginBottom: 24 }}>
                    {appraisal.employee_overall_comment
                      ? <Text style={{ whiteSpace: 'pre-wrap' }}>{appraisal.employee_overall_comment}</Text>
                      : <Text type="secondary">No employee comments provided.</Text>}
                  </Card>

                  <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>IV. Setting Next Year's Objectives</Text>} style={{ marginBottom: 24 }}>
                    {nextYearObjectives.length
                      ? nextYearObjectives.map((objective, index) => (
                        <Row key={index} gutter={12} style={{ marginBottom: 12 }}>
                          <Col span={4}><Tag color="green">{objective.priority || 0}%</Tag></Col>
                          <Col span={20}><Text style={{ whiteSpace: 'pre-wrap' }}>{objective.objective}</Text></Col>
                        </Row>
                      ))
                      : <Text type="secondary">No next-year objectives set yet.</Text>}
                    {nextYearObjectives.length > 0 && (
                      <Text type={Math.abs(nextYearTotal - 100) < 0.01 ? 'success' : 'danger'} strong>
                        Total Priority: {nextYearTotal}%
                      </Text>
                    )}
                  </Card>

                </>
              )}

            {renderApprovalHistory()}
          </>
        )}
        {children}
      </div>
    )
  }

  return (
    <div className='tti-appraisal-form' style={{ maxWidth: 1600, margin: '0 auto', padding: '20px', ...businessUnitThemeVars(theme) }}>
      <div style={{ marginBottom: 8 }}>
        <BackButton type='text' fallback='/dashboard' className='tti-appraisal-back-button' aria-label='Back' />
      </div>
      {signoffBanner}

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginBottom: 24
      }}>
        <div>
          {theme.logoUrl && <img src={theme.logoUrl}
            alt={appraisal.session_template_name || appraisal.session_business_unit_name || 'Template logo'}
            style={{
              display: 'block',
              maxHeight: 56,
              maxWidth: 160,
              objectFit: 'contain',
              marginBottom: 12
            }} />}
          <div>
            <Title level={2} className='tti-page-title' style={{ margin: 0 }}>{appraisal.session_display_title || '2026 Performance Appraisal'}</Title>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'flex-end', minWidth: 0, maxWidth: '100%' }}>
          <AppraisalPdfButton appraisalId={appraisal.id} status={appraisal.status} canExport={appraisal.can_export_pdf} type="primary">Export PDF</AppraisalPdfButton>
          <StatusBadge status={appraisal.status} label={appraisal.status_label} />
        </div>
      </div>

      {showReturnContext && returnContext}
      {showReturnContext && returnResponse}

      {renderAppraisalTabs(
        <>
          <Collapse
            className='tti-collapsible-form-card'
            style={{ marginBottom: 24 }}
            expandIconPlacement='end'
            items={[{
              key: 'rating-guide',
              label: <Space><InfoCircleOutlined /><Text strong>Rating Scale Guide</Text><Text type='secondary'>View scoring definitions</Text></Space>,
              children: isPrc ? <PrcRatingGuideDescription /> : <RatingGuideDescription source={ratingGuideSource} />
            }]}
          />

          {renderEmployeeInformation(
            <>
              <Row gutter={[24, 16]} className='tti-collapsible-fields'>
                {isPrc
                  ? prcEmployeeInfoFields.filter(field => field.key !== 'department_code').map(field => (
                    <Col span={8} key={field.key}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{field.label}:</Text>
                      <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                        {field.value}
                      </div>
                    </Col>
                  ))
                  : (
                    <>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Fullname:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {personLabel(appraisal.employee_first_name, appraisal.employee_last_name, appraisal.employee_email, '', appraisal.session_template)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Emp.ID:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.employee_company_id || '--'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Position:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.employee_position || '—'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Dept.:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.department_name || '—'}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Joined Date ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.employee_date_joined)}
                        </div>
                      </Col>

                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Review Period From ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.effective_review_period_start_date)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Review Period To ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.effective_review_period_end_date)}
                        </div>
                      </Col>
                      <Col span={8} />

                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluator
                          Name:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {personLabel(appraisal.approver_1_first_name ?? appraisal.manager_first_name, appraisal.approver_1_last_name ?? appraisal.manager_last_name, appraisal.approver_1_email ?? appraisal.manager_email, '', appraisal.session_template)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluation
                          Date ({DATE_FORMAT_LABEL}):</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {formatDate(appraisal.manager_submitted_at)}
                        </div>
                      </Col>
                      <Col span={8}>
                        <Text type="secondary" style={{ fontSize: 12, color: theme.primaryColor, fontStyle: 'normal' }}>Evaluator
                          ID:</Text>
                        <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                          {appraisal.manager_company_id || '--'}
                        </div>
                      </Col>
                    </>
                  )}
                {appraisal.session_organization_structure !== 'department_only' && (
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Business Unit:</Text>
                    <div style={{ borderBottom: '1px solid #d9d9d9', padding: '4px 0', minHeight: 28 }}>
                      {appraisal.business_unit_name || '—'}
                    </div>
                  </Col>
                )}
              </Row>
              {appraisal.current_manager_access && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  message={`Read-only access as current approver. ${firstApproverName} was assigned when this appraisal was created.`}
                />
              )}
            </>
          )}

          {isCheckboxFlow && (
            <OverallPerformanceRatingCheckboxes
              formData={formData}
              formConfig={formConfig}
              disabled
            />
          )}

          {appraisal.employee_submitted_at
            ? (
              <>
                <Card
                  className='tti-appraisal-section-card'
                  style={{ marginBottom: 24 }}
                  title={<Text strong style={{ color: '#FFF' }}>Annual Objectives Appraisal</Text>}
                  size="small"
                >
                  {(appraisal.session_template || appraisal.session_business_unit || '').includes('milwaukee') && (
                    <div style={{
                      marginBottom: 12,
                      padding: 12,
                      background: '#f9f9f9',
                      border: '1px solid #e8e8e8',
                      borderRadius: 4
                    }}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>List and prioritize your key objectives
                        following the S.M.A.R.T principles</Text>
                      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#595959' }}>
                        <li><Text strong>S - Specific:</Text> clear and well-defined (What exactly do I want to achieve?
                          Why is it important?)
                        </li>
                        <li><Text strong>M - Measurable:</Text> can be tracked using metrics or indicators (How will I
                          measure success? What evidence shows progress?)
                        </li>
                        <li><Text strong>A - Achievable:</Text> realistic and attainable (Can I reach this goal with my
                          current skills and resources?)
                        </li>
                        <li><Text strong>R - Relevant:</Text> aligns with team, department, or company objectives (Does
                          this goal support broader organizational priorities?)
                        </li>
                        <li><Text strong>T - Time-bound:</Text> clear deadline or timeframe for completion (When will I
                          achieve this goal? What are the milestones?)
                        </li>
                      </ol>
                    </div>
                  )}
                  <Text style={{ display: 'block', marginBottom: 16, color: 'var(--bu-ink)' }}>
                    {objectivesConfig.description || 'List key objectives and prioritize using percentage assigned. As a percent of total job, the priority total should add up to 100%.'}
                  </Text>

                  {objectives.length > 0
                    ? (
                      <>
                        <div style={{ marginBottom: 12, borderBottom: '1px solid var(--tti-light-grey)' }}>
                          <Row gutter={8}>
                            <Col span={2}>
                              <div style={{
                                padding: '8px 4px',
                                fontWeight: 600,
                                textAlign: 'center',
                                background: 'var(--neutral-grey1)',
                                // fontSize: 14
                              }}>{objectivesConfig.priority_label || '% Priority'}
                              </div>
                            </Col>
                            <Col span={isPrc ? 5 : 6}>
                              <div style={{
                                padding: '8px 4px',
                                fontWeight: 600,
                                textAlign: 'center',
                                background: 'var(--neutral-grey1)',
                                // fontSize: 14
                              }}>{isPrc ? prcObjectives.objective_label : 'Annual Performance Objectives'}</div>
                            </Col>
                            {isPrc && <Col span={5}>
                              <div style={{
                                padding: '8px 4px',
                                fontWeight: 600,
                                textAlign: 'center',
                                background: 'var(--neutral-grey1)',
                                // fontSize: 14
                              }}>{prcObjectives.success_indicators_label}</div>
                            </Col>}
                            <Col span={isPrc ? 6 : 8} style={{ borderLeft: '1px solid var(--neutral-grey3)' }}>
                              <div style={{
                                padding: '6px 0',
                                fontWeight: 600,
                                textAlign: 'center',
                                background: 'var(--neutral-grey2)',
                                // fontSize: 14
                              }}>{isPrc ? `Self-Evaluation ${objectiveScoreLabel}` : 'Self-Evaluation'}</div>
                              <Row gutter={4}>
                                <Col span={10}>
                                  <div style={{
                                    padding: '4px 0',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    background: 'var(--neutral-grey2)'
                                  }}>{isPrc ? 'Rating' : objectiveScoreLabel}</div>
                                </Col>
                                <Col span={14}>
                                  <div style={{
                                    padding: '4px 0',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    background: 'var(--neutral-grey2)'
                                  }}>Comment
                                  </div>
                                </Col>
                              </Row>
                            </Col>
                            <Col span={isPrc ? 6 : 8}>
                              <div style={{
                                padding: '6px 0',
                                fontWeight: 600,
                                textAlign: 'center',
                                background: 'var(--bu-secondary-bg)',
                                fontSize: 14
                              }}>Feedback by {firstApproverName}
                              </div>
                              <Row gutter={4}>
                                <Col span={10}>
                                  <div style={{
                                    padding: '4px 0',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    background: 'var(--bu-secondary-bg)'
                                  }}>{isPrc ? 'Rating' : objectiveScoreLabel}</div>
                                </Col>
                                <Col span={14}>
                                  <div style={{
                                    padding: '4px 0',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    background: 'var(--bu-secondary-bg)'
                                  }}>Comment
                                  </div>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>

                        {objectives.map((objective, index) => (
                          <div key={index} style={{
                            marginBottom: 12,
                            paddingBottom: 12,
                            borderBottom: index < objectives.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}>
                            <Row gutter={8} align="top">
                              <Col span={2}>
                                <div style={{
                                  padding: '6px',
                                  background: 'var(--neutral-grey0)',
                                  minHeight: 32,
                                  borderRadius: 4,
                                  textAlign: 'center',
                                  fontSize: 13
                                }}>
                                  {objectivePriorityDisplay(objective.priority)}
                                </div>
                              </Col>
                              <Col span={isPrc ? 5 : 6}>
                                <div style={{
                                  padding: '6px',
                                  background: 'var(--neutral-grey0)',
                                  minHeight: 60,
                                  borderRadius: 4,
                                  fontSize: 14
                                }}>
                                  <Text
                                    style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{objective.objective || '—'}</Text>
                                </div>
                              </Col>
                              {isPrc && (
                                <Col span={5}>
                                  <div style={{
                                    padding: '6px',
                                    background: 'var(--neutral-grey0)',
                                    minHeight: 60,
                                    borderRadius: 4,
                                    fontSize: 12
                                  }}>
                                    {prcSuccessIndicatorLevels.map(level => {
                                      const value = getPrcSuccessIndicatorValue(objective, level.key)
                                      return (
                                        <div key={level.key} style={{ marginBottom: 6 }}>
                                          <Text strong style={{ display: 'block', fontSize: 11 }}>{level.label}</Text>
                                          <Text type="secondary" style={{
                                            fontSize: 11,
                                            whiteSpace: 'pre-wrap'
                                          }}>{value || 'Not provided'}</Text>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </Col>
                              )}
                              <Col span={isPrc ? 6 : 8} style={{ borderLeft: '1px solid var(--neutral-grey3)' }}>
                                <Row gutter={4}>
                                  <Col span={10}>
                                    <div style={{
                                      padding: '6px',
                                      background: 'var(--neutral-grey0)',
                                      minHeight: 32,
                                      borderRadius: 4,
                                      textAlign: 'center',
                                      fontSize: 13
                                    }}>
                                      {getAppraisalRatingDisplay(objective.self_score) ||
                                        <Text type="secondary" style={{ fontSize: 13 }}>Not rated</Text>}
                                    </div>
                                  </Col>
                                  <Col span={14}>
                                    <div style={{
                                      padding: '6px',
                                      background: 'var(--neutral-grey0)',
                                      minHeight: 60,
                                      borderRadius: 4,
                                      fontSize: 14
                                    }}>
                                      <Text type="secondary" style={{
                                        fontSize: 14,
                                        whiteSpace: 'pre-wrap'
                                      }}>{objective.self_comment || 'No comment'}</Text>
                                    </div>
                                  </Col>
                                </Row>
                              </Col>
                              <Col span={isPrc ? 6 : 8}>
                                <Row gutter={4}>
                                  <Col span={10}>
                                    <div style={{
                                      padding: '6px',
                                      background: 'var(--neutral-grey0)',
                                      minHeight: 32,
                                      borderRadius: 4,
                                      textAlign: 'center',
                                      fontSize: 13
                                    }}>
                                      {getAppraisalRatingDisplay(objective.manager_score) || (
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                          {appraisal.manager_submitted_at ? 'Not rated' : 'Pending'}
                                        </Text>
                                      )}
                                    </div>
                                  </Col>
                                  <Col span={14}>
                                    <div style={{
                                      padding: '6px',
                                      background: 'var(--neutral-grey0)',
                                      minHeight: 60,
                                      borderRadius: 4,
                                      fontSize: 14
                                    }}>
                                      <Text type="secondary" style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                                        {objective.manager_comment || (appraisal.manager_submitted_at ? 'No comment' : 'Pending')}
                                      </Text>
                                    </div>
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </div>
                        ))}

                        {!usesObjectivePriorityOptions && (
                          <div style={{ marginTop: 8, marginBottom: 16 }}>
                            <Text type={hasValidPriorityTotal ? 'success' : 'danger'} strong>
                              Total Priority: {totalPriority}% {hasValidPriorityTotal ? '✓' : '(must equal 100%)'}
                            </Text>
                          </div>
                        )}

                        {objectiveSummaryEnabled && (
                          <div style={{
                            marginTop: 16,
                            paddingTop: 16,
                            borderTop: '2px solid #d9d9d9',
                            background: 'var(--neutral-grey0)',
                            padding: 16,
                            borderRadius: 4
                          }}>
                            <SuggestedRatingBand items={[
                              { label: 'Employee Calculated Suggested Rating', value: employeeObjectiveSuggestedRating },
                              { label: 'Manager Calculated Suggested Rating', value: managerObjectiveSuggestedRating }
                            ]} />
                            <Row gutter={8} align="middle" style={{ marginTop: 12 }}>
                              <Col span={8}><Text strong>Overall Rating of Objective Appraisal (manual)</Text></Col>
                              <Col span={8}>
                                <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                                  <Text strong>{manualRatingDisplay(employeeObjectiveRatingField)}</Text>
                                </div>
                              </Col>
                              <Col span={8}>
                                <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-secondary-bg)', borderRadius: 4 }}>
                                  <Text strong>{manualRatingDisplay(managerObjectiveRatingField)}</Text>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}

                        {showObjectiveAverageScore && (
                          <div style={{
                            marginTop: 16,
                            paddingTop: 16,
                            borderTop: '2px solid #d9d9d9',
                            background: 'var(--neutral-grey0)',
                            padding: 16,
                            borderRadius: 4
                          }}>
                            <Row gutter={8} align="middle">
                              <Col span={8}><Text strong>Average Score</Text></Col>
                              <Col span={8}>
                                <div style={{
                                  textAlign: 'center',
                                  padding: 10,
                                  background: 'var(--bu-primary-bg)',
                                  borderRadius: 4
                                }}>
                                  <Text strong style={{ display: 'block', fontSize: 12 }}>Employee Objective
                                    Average</Text>
                                  <Text strong style={{
                                    fontSize: 16,
                                    color: 'var(--tti-info)'
                                  }}>{objectiveSelfAvg || '--'}</Text>
                                  {objectiveSelfAvg && (
                                    <div>
                                      <Text type="secondary" style={{ fontSize: 14 }}>
                                        {isPrc ? prcEvaluationLevel(objectiveSelfAvg) : getRatingCategory(objectiveSelfAvg)?.rating}
                                      </Text>
                                    </div>
                                  )}
                                </div>
                              </Col>
                              <Col span={8}>
                                <div style={{
                                  textAlign: 'center',
                                  padding: 10,
                                  background: appraisal.manager_submitted_at ? 'var(--bu-secondary-bg)' : '#f0f0f0',
                                  borderRadius: 4
                                }}>
                                  <Text strong style={{ display: 'block', fontSize: 12 }}>Objective Average by {firstApproverName}</Text>
                                  <Text strong style={{
                                    fontSize: 16,
                                    color: appraisal.manager_submitted_at ? 'var(--tti-success)' : 'var(--tti-muted)'
                                  }}>{objectiveManagerAvg || '--'}</Text>
                                  {objectiveManagerAvg && (
                                    <div>
                                      <Text type="secondary" style={{ fontSize: 14 }}>
                                        {isPrc ? prcEvaluationLevel(objectiveManagerAvg) : getRatingCategory(objectiveManagerAvg)?.rating}
                                      </Text>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </>
                    )
                    : (
                      <Text type="secondary">No objectives were submitted for this appraisal.</Text>
                    )}
                </Card>

                {isCheckboxFlow
                  ? (
                    <PerformanceFactorsCard
                      formStructure={formStructure}
                      formConfig={formConfig}
                      getFieldValue={getFieldValue}
                      updateField={() => { }}
                      getQuestionRatingOptions={() => []}
                      getRatingOptionLabel={option => option.label}
                      getRatingValueDisplay={getAppraisalRatingDisplay}
                      editableSide={null}
                      managerSubmitted={Boolean(appraisal.manager_submitted_at)}
                    />
                  )
                  : formStructure.map((category) => (
                    <Card
                      className='tti-appraisal-section-card'
                      key={category.id}
                      style={{ marginBottom: 24 }}
                      title={(
                        <div>
                          <Text style={{ color: '#FFF' }}>{category.title}</Text>
                          {category.description && (
                            <Text type="secondary" style={{ display: 'block', marginTop: 4, whiteSpace: 'normal' }}>
                              {category.description}
                            </Text>
                          )}
                        </div>
                      )}
                      size="small"
                    >
                      {category.questions.map((question, qIdx) => {
                        const empRating = getFieldValue(category.id, question.id, 'employee_rating')
                        const empComments = getFieldValue(category.id, question.id, 'employee_comments')
                        const mgrRating = getFieldValue(category.id, question.id, 'manager_rating')
                        const mgrComments = getFieldValue(category.id, question.id, 'manager_comments')

                        return (
                          <div
                            key={question.id} style={{
                              marginBottom: 12,
                              paddingBottom: 12,
                              borderBottom: qIdx < category.questions.length - 1 ? '1px solid #f0f0f0' : 'none'
                            }}
                          >
                            <Paragraph strong style={{ marginBottom: 8, fontSize: 15, lineHeight: 1.5 }}>
                              {qIdx + 1}. {question.text}
                            </Paragraph>
                            <Row gutter={16}>
                              <Col span={12}>
                                <div style={{ padding: 8, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                                  <Text style={{ fontSize: 14 }}>
                                    <strong>Employee:</strong> {getAppraisalRatingDisplay(empRating)}
                                  </Text>
                                  {!singleCategoryComment && empComments && (
                                    <>
                                      <br />
                                      <Text type="secondary" style={{ fontSize: 14 }}>{empComments}</Text>
                                    </>
                                  )}
                                </div>
                              </Col>
                              <Col span={12}>
                                {appraisal.manager_submitted_at
                                  ? (
                                    <div style={{ padding: 8, background: 'var(--bu-secondary-bg)', borderRadius: 4 }}>
                                      <Text style={{ fontSize: 14 }}>
                                        <strong>{firstApproverName}:</strong> {getAppraisalRatingDisplay(mgrRating)}
                                      </Text>
                                      {!singleCategoryComment && mgrComments && (
                                        <>
                                          <br />
                                          <Text type="secondary" style={{ fontSize: 14 }}>{mgrComments}</Text>
                                        </>
                                      )}
                                    </div>
                                  )
                                  : (
                                    <div style={{ padding: 8, background: 'var(--neutral-grey0)', borderRadius: 4 }}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>Pending manager review</Text>
                                    </div>
                                  )}
                              </Col>
                            </Row>
                          </div>
                        )
                      })}

                      {singleCategoryComment && category.questions.some(question => question.id === categoryCommentQuestionKey) && (
                        <div style={{ marginTop: 16 }}>
                          <Text strong style={{ display: 'block', marginBottom: 8 }}>{categoryCommentLabel}</Text>
                          <Row gutter={16}>
                            <Col span={12}>
                              <div style={{ padding: 8, background: 'var(--bu-primary-bg)', borderRadius: 4, minHeight: 60 }}>
                                <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                                  {getFieldValue(category.id, categoryCommentQuestionKey, 'employee_comments') || 'No comment'}
                                </Text>
                              </div>
                            </Col>
                            <Col span={12}>
                              <div style={{ padding: 8, background: 'var(--bu-secondary-bg)', borderRadius: 4, minHeight: 60 }}>
                                <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                                  {appraisal.manager_submitted_at
                                    ? (getFieldValue(category.id, categoryCommentQuestionKey, 'manager_comments') || 'No comment')
                                    : 'Pending manager review'}
                                </Text>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      )}

                      {/* Category Averages */}
                      <CalculatedRatingBand label={categoryAverageLabel} items={[
                        {
                          label: 'Employee Average',
                          value: showCategoryAverageAsRating
                            ? calculateCategorySuggestedRating(category.id, 'employee_rating')
                            : calculateCategoryAverage(category.id, 'employee_rating'),
                          detail: !showCategoryAverageAsRating && calculateCategoryAverage(category.id, 'employee_rating')
                            ? (isPrc
                                ? prcEvaluationLevel(calculateCategoryAverage(category.id, 'employee_rating'))
                                : getRatingCategory(calculateCategoryAverage(category.id, 'employee_rating'))?.rating)
                            : null
                        },
                        {
                          label: `Average Rating by ${firstApproverName}`,
                          value: appraisal.manager_submitted_at
                            ? (showCategoryAverageAsRating
                                ? calculateCategorySuggestedRating(category.id, 'manager_rating')
                                : calculateCategoryAverage(category.id, 'manager_rating'))
                            : 'Pending manager review',
                          detail: appraisal.manager_submitted_at && !showCategoryAverageAsRating && calculateCategoryAverage(category.id, 'manager_rating')
                            ? (isPrc
                                ? prcEvaluationLevel(calculateCategoryAverage(category.id, 'manager_rating'))
                                : getRatingCategory(calculateCategoryAverage(category.id, 'manager_rating'))?.rating)
                            : null
                        }
                      ]} />
                    </Card>
                  ))}

                <Card
                  className='tti-appraisal-section-card'
                  style={{ marginBottom: 24 }}
                  title={<Text style={{ color: '#FFF' }}>{competencySummaryTitle}{competencySummarySubtitle && <><br />({competencySummarySubtitle})</>}</Text>}
                  size="small"
                >
                  {showCompetencySummarySubtext && (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                      Calculated as the average of all competency category ratings.
                    </Text>
                  )}
                  <SuggestedRatingBand items={[
                    {
                      label: 'Employee Calculated Suggested Rating',
                      value: showCategoryAverageAsRating ? employeeCompetencySuggestedRating : overallEmployeeCategoryAvg,
                      detail: !showCategoryAverageAsRating && overallEmployeeCategoryAvg
                        ? getAppraisalRatingCategoryDisplay(overallEmployeeCategoryAvg)
                        : null
                    },
                    {
                      label: 'Manager Calculated Suggested Rating',
                      value: showCategoryAverageAsRating ? managerCompetencySuggestedRating : overallManagerCategoryAvg,
                      detail: !showCategoryAverageAsRating && overallManagerCategoryAvg
                        ? getAppraisalRatingCategoryDisplay(overallManagerCategoryAvg)
                        : null
                    }
                  ]} />
                  {competencyManualRating.enabled && (
                    <Row gutter={16} style={{ marginTop: 12 }}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                          <Text strong style={{ display: 'block', marginBottom: 6 }}>{competencySummaryTitle} (Employee manual)</Text>
                          <Text>{manualRatingDisplay(employeeCompetencyRatingField)}</Text>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-secondary-bg)', borderRadius: 4 }}>
                          <Text strong style={{ display: 'block', marginBottom: 6 }}>{competencySummaryTitle} (Manager manual)</Text>
                          <Text>{manualRatingDisplay(managerCompetencyRatingField)}</Text>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card>

                <Card
                  className='tti-appraisal-section-card'
                  style={{ marginBottom: 24 }}
                  title={<Text strong style={{ color: '#FFF' }}>{overallPerformanceTitle}</Text>}
                  size="small"
                >
                  {showOverallSummarySubtext && (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                      Calculated equally from the weighted Objectives score and Competency score.
                    </Text>
                  )}
                  <SuggestedRatingBand items={[
                    {
                      label: 'Employee Calculated Suggested Rating',
                      value: showCategoryAverageAsRating ? employeeOverallSuggestedRating : overallEmployeeAverage,
                      detail: !showCategoryAverageAsRating && overallEmployeeAverage
                        ? getAppraisalRatingCategoryDisplay(overallEmployeeAverage)
                        : null
                    },
                    {
                      label: 'Manager Calculated Suggested Rating',
                      value: showCategoryAverageAsRating ? managerOverallSuggestedRating : overallManagerAverage,
                      detail: !showCategoryAverageAsRating && overallManagerAverage
                        ? getAppraisalRatingCategoryDisplay(overallManagerAverage)
                        : null
                    }
                  ]} />
                  {overallManualRating.enabled && (
                    <Row gutter={16} style={{ marginTop: 12 }}>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-primary-bg)', borderRadius: 4 }}>
                          <Text strong style={{ display: 'block', marginBottom: 6 }}>{overallPerformanceTitle} (Employee manual)</Text>
                          <Text>{manualRatingDisplay(employeeOverallRatingField)}</Text>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ textAlign: 'center', padding: 10, background: 'var(--bu-secondary-bg)', borderRadius: 4 }}>
                          <Text strong style={{ display: 'block', marginBottom: 6 }}>{overallPerformanceTitle} (Manager manual)</Text>
                          <Text>{manualRatingDisplay(managerOverallRatingField)}</Text>
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card>

                {isCheckboxFlow && (
                  <CultureValuesCheckboxes
                    formData={formData}
                    formConfig={formConfig}
                    disabled
                  />
                )}

                {isCheckboxFlow && (
                  <NextYearObjectivesCard
                    formData={formData}
                    formConfig={formConfig}
                    disabled
                  />
                )}

                {(hasCommentText(appraisal.employee_overall_comment) || hasCommentText(appraisal.manager_overall_comment)) && (
                  <Card
                    className='tti-appraisal-section-card'
                    title={<Text style={{ color: '#FFF' }}>Overall Comments</Text>}
                    style={{ marginBottom: 24 }}
                    size='small'
                  >
                    <Row gutter={16}>
                      {hasCommentText(appraisal.employee_overall_comment) && (
                        <Col span={hasCommentText(appraisal.manager_overall_comment) ? 12 : 24}>
                          <div style={{ marginBottom: 8 }}>
                            <Text strong>Employee Overall Comment</Text>
                          </div>
                          <div style={{ padding: 12, background: 'var(--bu-primary-bg)', minHeight: 60, borderRadius: 4 }}>
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{appraisal.employee_overall_comment}</Text>
                          </div>
                        </Col>
                      )}
                      {hasCommentText(appraisal.manager_overall_comment) && (
                        <Col span={hasCommentText(appraisal.employee_overall_comment) ? 12 : 24}>
                          <div style={{ marginBottom: 8 }}>
                            <Text strong>Overall Comment by {firstApproverName}</Text>
                          </div>
                          <div style={{ padding: 12, background: 'var(--bu-secondary-bg)', minHeight: 60, borderRadius: 4 }}>
                            <Text style={{ whiteSpace: 'pre-wrap' }}>{appraisal.manager_overall_comment}</Text>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Card>
                )}

              </>
            ) : (
              <Card size="small" style={{ marginBottom: 24 }}>
                <Text type="secondary">Employee self-assessment not yet submitted.</Text>
              </Card>
            )}

          {appraisal.approver_2 && (appraisal.head_comments || appraisal.head_signed_off_at) && (
            <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Sign-off by {secondApproverName}</Text>} size="small"
              style={{ marginBottom: 24, background: 'var(--tti-blue-light)' }}>
              <Descriptions column={1} size="small"
                labelStyle={{ color: 'var(--tti-muted)', width: 160, fontWeight: 500 }}>
                {hasCommentText(appraisal.head_comments) && (
                  <Descriptions.Item label="Comments">{appraisal.head_comments}</Descriptions.Item>
                )}
                <Descriptions.Item
                  label={`Signed off (${DATE_TIME_FORMAT_LABEL})`}>{formatDateTime(appraisal.head_signed_off_at)}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {appraisal.approver_3 && (appraisal.bu_head_comments || appraisal.bu_head_signed_off_at) && (
            <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Sign-off by {thirdApproverName}</Text>} size="small"
              style={{ marginBottom: 24, background: 'var(--tti-blue-light)' }}>
              <Descriptions column={1} size="small"
                labelStyle={{ color: 'var(--tti-muted)', width: 160, fontWeight: 500 }}>
                {hasCommentText(appraisal.bu_head_comments) && (
                  <Descriptions.Item label="Comments">{appraisal.bu_head_comments}</Descriptions.Item>
                )}
                <Descriptions.Item
                  label={`Signed off (${DATE_TIME_FORMAT_LABEL})`}>{formatDateTime(appraisal.bu_head_signed_off_at)}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {renderApprovalHistory()}
        </>
      )}
      {children}

    </div>
  )
}